import React, { useState } from "react";
import { FlexRow } from "ui-kit/Layout/Flex";
import FlowInput from "components/Signup/components/FlowInput/FlowInput";
import { StepProps } from "../../types";
import ChatMessage, { ChatContainer } from "ui-kit/ChatMessage/ChatMessage";
import { Typography } from "ui-kit/Typography";
import Gutter from "ui-kit/Gutter";
import {
  formatErrorMessage,
  validateEmail,
  validatePhone,
} from "utils/validation";
import { Trans, useWebTranslation } from "translations";
import { useSignupDispatch } from "components/Signup/store/hooks";
import { updateCustomer } from "components/Signup/store/slices/customer";
import ErrorMessage from "components/ErrorMessage/ErrorMessage";
import FlowButtons from "components/Signup/components/FlowButtons/FlowButtons";
import AcceptBox from "components/Signup/components/AcceptBoxFra/AcceptBox";
import { useCustomer } from "components/Signup/store/selectors/customer";
import { useError } from "components/Signup/store/selectors/metadata";
import Colors from "ui-kit/colors";

const UserEmail = ({ previous, next, isValid, transitioning }: StepProps) => {
  const { t } = useWebTranslation();
  const [phoneValid, setPhoneValid] = useState(true);
  const [emailValid, setEmailValid] = useState(true);

  const dispatch = useSignupDispatch();
  const { email, privacyAccepted, marketingPermissionAccepted, phone } =
    useCustomer();
  const error = useError();

  return (
    <ChatContainer leaving={transitioning}>
      <ChatMessage>
        <Typography>{t("signup.user_email.text")}</Typography>
        <Gutter size={1.5} />
        <Typography color="grey500">
          {t("signup.user_email.description")}
        </Typography>
      </ChatMessage>
      <Gutter size={2} />
      <ChatMessage fromMe>
        <FlexRow
          alignItems="baseline"
          style={{ position: "relative", width: 300 }}
        >
          <FlowInput
            placeholder={t("global.email_optional")}
            onChange={(email) => {
              if (!emailValid) {
                setEmailValid(validateEmail(email));
              }
              dispatch(updateCustomer({ email }));
            }}
            onEnter={() => isValid && next()}
            onBlur={(e) => {
              if (e.currentTarget.value.trim().length === 0) {
                setEmailValid(true);
              } else {
                setEmailValid(validateEmail(e.target.value));
              }
            }}
            value={email ?? undefined}
            style={{
              width: "100%",
              paddingLeft: 8,
            }}
          />
        </FlexRow>
      </ChatMessage>
      {!emailValid && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: 10,
          }}
        >
          <Gutter size={1} />
          <Typography color={"error"} testId="date-error">
            {t("validation.email")}
          </Typography>
          <Gutter size={1} />
        </div>
      )}
      <ChatMessage fromMe>
        <FlexRow
          alignItems="baseline"
          style={{ position: "relative", width: 300 }}
        >
          <FlowInput
            placeholder={t("global.phone_optional")}
            onChange={(phone) => {
              const formattedPhone = phone.replace(/[^\d+-\s]+/g, "");

              const validatedPhone = validatePhone(formattedPhone);

              if (!phoneValid) {
                setPhoneValid(validatedPhone);
              }

              if (formattedPhone.length === 0) {
                setPhoneValid(true);
              }

              dispatch(updateCustomer({ phone: formattedPhone }));
            }}
            onBlur={(e) => {
              if (e.currentTarget.value.trim().length === 0) {
                setPhoneValid(true);
              } else {
                setPhoneValid(validatePhone(e.target.value));
              }
            }}
            onEnter={() => isValid && next()}
            value={phone ?? undefined}
            style={{
              width: "100%",
              paddingLeft: 8,
            }}
          />
        </FlexRow>
      </ChatMessage>
      {!phoneValid && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: 10,
          }}
        >
          <Gutter size={1} />
          <Typography color={"error"} testId="date-error" align="right">
            {t("validation.phone")}
          </Typography>
          <Gutter size={1} />
        </div>
      )}
      {!!error && (
        <ErrorMessage>
          <Typography color="error">{formatErrorMessage(error)}</Typography>
        </ErrorMessage>
      )}
      <Gutter size={2} />
      <ChatMessage
        fromMe
        border={false}
        gutters={false}
        style={{ width: 380, maxWidth: "100%", marginLeft: "auto" }}
      >
        <AcceptBox
          textKey="signup.user_info.marketing_notice.description"
          links={[]}
          selected={marketingPermissionAccepted}
          onSelect={() => {
            dispatch(
              updateCustomer({
                marketingPermissionAccepted: !marketingPermissionAccepted,
              })
            );
          }}
        />
        <Gutter size={3} />
      </ChatMessage>
      <Typography
        variant="body3"
        color="grey500"
        style={{ display: "block", paddingLeft: 32, width: "100%" }}
      >
        <Trans
          i18nKey="signup.user_info.terms_notice.description"
          components={[
            "https://docs.lassie.co/fra/latest/cgu.pdf",
            "https://www.lassie.fr/politique-de-confidentialite",
          ].map((href) => (
            <a
              key={0}
              href={href}
              style={{
                textDecoration: "underline",
                color: Colors.blue300,
              }}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            />
          ))}
        />
      </Typography>
      <FlowButtons
        inProgress={transitioning}
        next={next}
        disabled={!isValid}
        previous={previous}
        backButton={false}
      />
    </ChatContainer>
  );
};

export default UserEmail;
